<template>
  <div class="page">
    <div class="title-row">
      <span class="page-title">班组管理</span>
      <el-button type="success" size="small" @click="openAddGroupModal">
        <i class="el-icon-plus"></i> 新建班组
      </el-button>
    </div>

    <!-- 搜索栏 -->
    <div class="filter-bar">
      <el-input v-model="searchName" placeholder="搜索班组名称" clearable size="small" style="width:200px" @keyup.enter.native="loadGroups"></el-input>
      <el-button type="primary" size="small" :loading="loading" @click="loadGroups" style="margin-left:8px">
        <i class="el-icon-search"></i> 查询
      </el-button>
      <el-button size="small" @click="resetSearch" style="margin-left:6px">重置</el-button>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" style="display:flex;justify-content:center;padding:40px">
      <el-spinner></el-spinner>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!groupList.length" class="group-empty">
      <i class="el-icon-user" style="font-size:48px;color:#CBD5E1;margin-bottom:12px"></i>
      <div style="color:#94A3B8;font-size:14px">暂无班组，点击右上角新建</div>
    </div>

    <!-- 卡片网格 -->
    <div v-else class="group-grid">
      <div v-for="g in groupList" :key="g.id" class="group-card">
        <div class="gc-header">
          <div class="gc-icon"><i class="el-icon-user-solid" style="font-size:18px;color:#fff"></i></div>
          <div class="gc-info">
            <div class="gc-name">{{ g.name }}</div>
            <div class="gc-meta">
              <span>班长：{{ g.realName || '未设置' }}</span>
              <span class="gc-sep">·</span>
              <span>{{ g.memberCount || 0 }} 人</span>
            </div>
          </div>
          <el-dropdown trigger="click">
            <el-button type="text" size="small" class="gc-more-btn"><i class="el-icon-more"></i></el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item @click.native="openEditGroupModal(g)"><i class="el-icon-edit"></i> 修改名称</el-dropdown-item>
              <el-dropdown-item @click.native="openMembersModal(g)"><i class="el-icon-user"></i> 管理成员</el-dropdown-item>
              <el-dropdown-item class="danger-item" @click.native="handleDeleteGroup(g)"><i class="el-icon-delete"></i> 删除班组</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
        <!-- 成员头像 -->
        <div class="gc-members" @click="openMembersModal(g)">
          <template v-if="g.members && g.members.length">
            <div
              v-for="m in g.members.slice(0, 7)"
              :key="m.nursingUserId"
              class="gc-avatar"
              :style="{ background: getAvatarColor(m.nursingUserId) }"
              :title="m.realName">
              {{ m.realName ? m.realName.charAt(0) : '?' }}
            </div>
            <div v-if="g.members.length > 7" class="gc-avatar gc-avatar-more">+{{ g.members.length - 7 }}</div>
          </template>
          <div v-else class="gc-no-member"><i class="el-icon-user-solid" style="font-size:13px;margin-right:4px"></i>点击添加成员</div>
        </div>
        <div class="gc-footer">
          <el-button size="mini" type="text" @click="openMembersModal(g)"><i class="el-icon-user"></i> 管理成员</el-button>
          <el-button size="mini" type="text" @click="openEditGroupModal(g)"><i class="el-icon-edit"></i> 编辑</el-button>
        </div>
      </div>
    </div>

    <!-- 新建/编辑班组弹窗 -->
    <el-dialog
      :title="editingGroup ? '修改班组名称' : '新建班组'"
      :visible.sync="groupModalVisible"
      width="480px"
      :append-to-body="true">
      <el-form :model="groupForm" label-width="80px">
        <el-form-item label="班组名称" required>
          <el-input v-model="groupForm.name" placeholder="如：一组、早班组、A组" clearable></el-input>
        </el-form-item>
        <template v-if="!editingGroup">
          <el-form-item label="班长" required>
            <el-select v-model="groupForm.leaderId" placeholder="请选择班长" filterable style="width:100%">
              <el-option v-for="s in allStaffList" :key="s.userId" :value="s.userId" :label="s.realName || s.userName"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="初始成员">
            <el-select v-model="groupForm.memberIds" placeholder="选择成员（可多选）" multiple filterable style="width:100%">
              <el-option v-for="s in allStaffList" :key="s.userId" :value="s.userId" :label="s.realName || s.userName"></el-option>
            </el-select>
            <div style="font-size:12px;color:#94A3B8;margin-top:4px">班长会自动加入成员列表</div>
          </el-form-item>
        </template>
      </el-form>
      <div slot="footer">
        <el-button @click="groupModalVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitGroupForm">保存</el-button>
      </div>
    </el-dialog>

    <!-- 成员管理弹窗 -->
    <el-dialog
      :title="(currentGroup ? currentGroup.name : '') + ' · 成员管理'"
      :visible.sync="membersModalVisible"
      width="560px"
      :append-to-body="true">
      <!-- 添加成员 -->
      <div style="display:flex;margin-bottom:14px">
        <el-select v-model="addMemberIds" placeholder="搜索并选择要添加的护工" multiple filterable style="flex:1">
          <el-option v-for="s in availableStaff" :key="s.userId" :value="s.userId" :label="s.realName || s.userName"></el-option>
        </el-select>
        <el-button type="primary" :loading="addingMember" :disabled="!addMemberIds.length" @click="handleAddMembers" style="margin-left:8px">
          <i class="el-icon-plus"></i> 添加
        </el-button>
      </div>
      <!-- 成员表格 -->
      <el-table :data="currentMembers" size="small" :border="false" stripe row-key="nursingUserId">
        <el-table-column type="index" label="序号" width="56" align="center"></el-table-column>
        <el-table-column label="姓名" width="120">
          <template slot-scope="{ row }">
            <div style="display:flex;align-items:center;">
              <span class="member-avatar-sm" :style="{ background: getAvatarColor(row.nursingUserId) }">
                {{ row.realName ? row.realName.charAt(0) : '?' }}
              </span>
              <span style="margin-left:8px">{{ row.realName }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="角色" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag v-if="isLeader(row)" type="primary" size="mini">班长</el-tag>
            <span v-else style="font-size:12px;color:#94A3B8">护理员</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center">
          <template slot-scope="{ row }">
            <el-button v-if="!isLeader(row)" type="text" size="mini" @click="handleSetLeader(row)">
              <i class="el-icon-star-off"></i> 设为班长
            </el-button>
            <el-popconfirm title="确认将该护工移出班组？" @confirm="handleRemoveMember(row)">
              <el-button slot="reference" type="text" size="mini" style="color:#EF4444;margin-left:4px" :disabled="isLeader(row)">移出</el-button>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button type="primary" @click="membersModalVisible = false">完成</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getNursingGroupList, getNursingGroupMembers,
  addNursingGroup, updateNursingGroupName, deleteNursingGroup,
  addNursingGroupMembers, removeNursingMember, updateNursingGroupLeader,
  getNursingStaffList, getAllNursingStaff
} from './scheduleApi'
import { getAvatarColor } from './scheduleUtils'

export default {
  name: 'NursingGroup',
  data() {
    return {
      loading: false,
      groupList: [],
      allStaffList: [],
      searchName: '',
      groupModalVisible: false,
      editingGroup: null,
      submitting: false,
      groupForm: { name: '', leaderId: '', memberIds: [] },
      membersModalVisible: false,
      currentGroup: null,
      currentMembers: [],
      addMemberIds: [],
      addingMember: false
    }
  },
  computed: {
    availableStaff() {
      const existIds = new Set(this.currentMembers.map(m => String(m.nursingUserId || m.userId)))
      return this.allStaffList.filter(s => !existIds.has(String(s.userId)))
    }
  },
  mounted() {
    this.loadGroups()
    this.loadStaff()
  },
  methods: {
    getAvatarColor,
    isLeader(m) {
      return this.currentGroup && String(m.nursingUserId || m.userId) === String(this.currentGroup.userId)
    },
    loadGroups() {
      this.loading = true
      getNursingGroupList(this.searchName || undefined).then(async res => {
        if (res && res.code == 200) {
          const data = res.result || res
          const list = Array.isArray(data) ? data : (data.list || data.records || [])
          for (const g of list) {
            try {
              const mRes = await getNursingGroupMembers(g.id)
              if (mRes && mRes.code == 200) {
                const mData = mRes.result || mRes
                const members = mData.list || mData.data || (Array.isArray(mData) ? mData : [])
                g.members = members; g.memberCount = members.length
              } else { g.members = []; g.memberCount = 0 }
            } catch { g.members = []; g.memberCount = 0 }
          }
          this.groupList = list
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    resetSearch() { this.searchName = ''; this.loadGroups() },
    async loadStaff(keyword) {
      if (!keyword) {
        const res = await getAllNursingStaff()
        if (res && res.code == 200) {
          const data = res.result || res
          this.allStaffList = Array.isArray(data) ? data : (data.list || data.records || [])
          return
        }
      }
      const res = await getNursingStaffList({ pageNum: 1, pageSize: 500, userName: keyword || undefined })
      if (res && res.code == 200) {
        const data = res.result || res
        this.allStaffList = data.list || data.records || (Array.isArray(data) ? data : [])
      }
    },
    openAddGroupModal() {
      this.editingGroup = null
      Object.assign(this.groupForm, { name: '', leaderId: '', memberIds: [] })
      this.groupModalVisible = true
    },
    openEditGroupModal(g) {
      this.editingGroup = g
      Object.assign(this.groupForm, { name: g.name, leaderId: '', memberIds: [] })
      this.groupModalVisible = true
    },
    submitGroupForm() {
      if (!this.groupForm.name.trim()) { this.$message.warning('请输入班组名称'); return }
      this.submitting = true
      if (this.editingGroup) {
        updateNursingGroupName({ nursingGroupId: this.editingGroup.id, name: this.groupForm.name }).then(res => {
          if (res && res.code == 200) { this.$message.success('修改成功'); this.groupModalVisible = false; this.loadGroups() }
          this.submitting = false
        }).catch(() => { this.submitting = false })
      } else {
        if (!this.groupForm.leaderId) { this.$message.warning('请选择班长'); this.submitting = false; return }
        const memberSet = new Set([this.groupForm.leaderId, ...this.groupForm.memberIds])
        addNursingGroup({
          name: this.groupForm.name,
          userId: this.groupForm.leaderId,
          userList: [...memberSet].map(id => ({ userid: id }))
        }).then(res => {
          if (res && res.code == 200) { this.$message.success('创建成功'); this.groupModalVisible = false; this.loadGroups() }
          this.submitting = false
        }).catch(() => { this.submitting = false })
      }
    },
    handleDeleteGroup(g) {
      this.$confirm(`确认删除班组「${g.name}」？`, '确认删除', {
        confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning'
      }).then(async () => {
        const res = await deleteNursingGroup(g.id)
        if (res && res.code == 200) { this.$message.success('删除成功'); this.loadGroups() }
      }).catch(() => {})
    },
    async openMembersModal(g) {
      this.currentGroup = g
      this.addMemberIds = []
      this.membersModalVisible = true
      await this.refreshMembers()
    },
    async refreshMembers() {
      if (!this.currentGroup) return
      const res = await getNursingGroupMembers(this.currentGroup.id)
      if (res && res.code == 200) {
        const data = res.result || res
        this.currentMembers = data.list || data.data || (Array.isArray(data) ? data : [])
      }
    },
    handleAddMembers() {
      if (!this.addMemberIds.length) return
      this.addingMember = true
      addNursingGroupMembers({
        nursingGroupId: this.currentGroup.id,
        userList: this.addMemberIds.map(id => ({ userid: id }))
      }).then(res => {
        if (res && res.code == 200) {
          this.$message.success('添加成功')
          this.addMemberIds = []
          this.refreshMembers()
          this.loadGroups()
        }
        this.addingMember = false
      }).catch(() => { this.addingMember = false })
    },
    async handleRemoveMember(m) {
      const res = await removeNursingMember(m.nursingUserId || m.userId)
      if (res && res.code == 200) {
        this.$message.success('已移出班组')
        await this.refreshMembers()
        this.loadGroups()
      }
    },
    async handleSetLeader(m) {
      const memberId = m.nursingUserId || m.userId
      const res = await updateNursingGroupLeader({ nursingGroupId: this.currentGroup.id, userId: memberId })
      if (res && res.code == 200) {
        this.currentGroup = { ...this.currentGroup, userId: memberId }
        this.$message.success(`已将 ${m.realName} 设为班长`)
        await this.refreshMembers()
        this.loadGroups()
      }
    }
  }
}
</script>

<style scoped>
.page { padding: 0 16px; height: calc(100vh - 56px); display: flex; flex-direction: column; overflow: hidden; }
.title-row { display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; padding: 8px 0 4px; }
.page-title { font-size: 18px; font-weight: 700; color: #1E293B; }
.filter-bar { display: flex; align-items: center; flex-shrink: 0; padding: 8px 0; border-bottom: 1px solid #E2E8F0; margin-bottom: 12px; }
.group-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 0; }
.group-grid { flex: 1; overflow-y: auto; display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); grid-gap: 14px; align-content: start; }
.group-card { background: #fff; border-radius: 12px; border: 1px solid #E8EDF3; box-shadow: 0 1px 4px rgba(0,0,0,0.05); display: flex; flex-direction: column; overflow: hidden; }
.group-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.1); transform: translateY(-1px); }
.gc-header { display: flex; align-items: center; padding: 14px 14px 10px; }
.gc-icon { width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0; background: linear-gradient(135deg, #60A5FA, #2563EB); display: flex; align-items: center; justify-content: center; margin-right: 10px; }
.gc-info { flex: 1; min-width: 0; }
.gc-name { font-size: 15px; font-weight: 700; color: #1E293B; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gc-meta { display: flex; align-items: center; font-size: 12px; color: #94A3B8; margin-top: 3px; }
.gc-sep { color: #CBD5E1; margin: 0 4px; }
.gc-more-btn { color: #94A3B8; flex-shrink: 0; }
.gc-members { display: flex; align-items: center; padding: 10px 14px; min-height: 52px; border-top: 1px solid #F1F5F9; border-bottom: 1px solid #F1F5F9; cursor: pointer; }
.gc-members:hover { background: #F8FAFC; }
.gc-avatar { width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; border: 2px solid #fff; margin-left: -8px; }
.gc-avatar:first-child { margin-left: 0; }
.gc-avatar-more { background: #F1F5F9; color: #64748B; font-size: 11px; }
.gc-no-member { display: flex; align-items: center; font-size: 12px; color: #94A3B8; }
.gc-footer { display: flex; align-items: center; justify-content: flex-end; padding: 6px 10px; }
.member-avatar-sm { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; color: #fff; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
</style>

<style>
.danger-item { color: #EF4444 !important; }
</style>
